import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Lock, User, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api, { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    logo_url: '',
    logo_visible: true,
    site_name: 'BMS',
    site_name_full: 'BrightMind Software'
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const logoUrl = settings.logo_url ? 
    (settings.logo_url.startsWith('http') ? settings.logo_url : `${API_URL}${settings.logo_url}`) : 
    null;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-gray-50" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            {settings.logo_visible && logoUrl ? (
              <img 
                src={logoUrl} 
                alt={settings.site_name}
                className="h-12 w-auto object-contain"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-bms-primary flex items-center justify-center">
                <Brain className="w-7 h-7 text-white" />
              </div>
            )}
            <span className="text-2xl font-bold text-bms-text">{settings.site_name} Admin</span>
          </div>
          <p className="text-bms-text-muted">Yönetim paneline giriş yapın</p>
        </div>

        {/* Login Form */}
        <div className="p-8 rounded-2xl bg-white border border-gray-200 shadow-corporate">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-4 rounded-lg bg-red-50 text-red-600 mb-6 border border-red-100"
            >
              <AlertCircle className="w-5 h-5" />
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-bms-text mb-2">Kullanıcı Adı</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="admin"
                  className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 
                           focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-bms-text mb-2">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 
                           focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                />
              </div>
            </div>
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full px-8 py-4 bg-bms-primary hover:bg-bms-primary-dark text-white rounded-lg 
                       font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </motion.button>
          </form>
        </div>

        {/* Default credentials info */}
        <p className="text-center text-bms-text-muted text-sm mt-6">
          Varsayılan: admin / admin123
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
